import {
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { fetchAgentStatus } from '../agents/api';
import { fetchOpenAlertCount } from '../alerts/api';
import { ApiError } from '../api/errors';
import { useAppBar } from '../app/AppBar';
import type { Page } from '../api/pagination';
import { SESSION_QUERY_KEY } from '../auth/SessionProvider';
import { countByIntent, NOTE_INTENT_LABELS, NOTE_INTENTS, noteIntent, statusesForIntent } from '../notes/intents';
import type { NoteSummary } from '../notes/schema';
import { NOTE_STATUS_LABELS, type NoteStatus } from '../notes/status';
import { fetchReferenceCapture } from '../references/api';
import { ErrorScreen } from '../shell/ErrorScreen';
import { SegmentedControl } from '../ui/SegmentedControl';
import { Sheet } from '../ui/Sheet';
import { fetchNotes, restoreNote, retryNote } from './api';
import styles from './history.module.css';
import { withUpdatedNoteStatus, withUpdatedNoteStatusInPage } from './notesCache';
import {
  DISCARDED_FILTER,
  type NotesFilter,
  readNotesScreenState,
  saveNotesScreenState,
} from './notesScreenStore';
import { NoteRow, type RetryRowState } from './NoteRow';
import { computeRefetchIntervalMs } from './pollingCadence';
import { classifyRetryFailure } from './retryClassification';
import { agentIndicator, referenceIndicator } from './statusIndicators';

const PAGE_SIZE = 20;

/** Drops one entry immutably - `eslint`'s `no-dynamic-delete` refuses `delete obj[id]` for a
 * dynamically computed key, since it can deoptimize the object's hidden class. */
function withoutRetryState(
  states: Record<string, RetryRowState>,
  noteId: string,
): Record<string, RetryRowState> {
  return Object.fromEntries(Object.entries(states).filter(([id]) => id !== noteId));
}

/**
 * The "Notas" dashboard: every uploaded note, newest first, with its current status always
 * visible (CLAUDE.md: "the note's status is always visible later"). Grouped by the three
 * owner intents (mockup principle: exact statuses moved into the "Filtrar" sheet, so that
 * capability is not removed, only relocated) and system health collapsed into a header pill.
 */
export function NotesScreen() {
  const queryClient = useQueryClient();
  const restored = readNotesScreenState();
  const [filter, setFilter] = useState<NotesFilter>(restored?.filter ?? 'por_hacer');
  const [retryStates, setRetryStates] = useState<Record<string, RetryRowState>>({});
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [systemSheetOpen, setSystemSheetOpen] = useState(false);
  const [enCaminoExplainerOpen, setEnCaminoExplainerOpen] = useState(false);
  const pollAttemptRef = useRef(0);

  useAppBar(() => ({ title: 'Notas' }), []);
  const scrollYRef = useRef(restored?.scrollY ?? 0);
  const restoredScroll = useRef(false);

  const isIntentValue = (value: NotesFilter): value is (typeof NOTE_INTENTS)[number] =>
    (NOTE_INTENTS as readonly string[]).includes(value);
  // The discarded list is its own page, not a slice of the live one: a discarded note keeps the
  // status it had, so no status filter could separate the two.
  const showingDiscarded = filter === DISCARDED_FILTER;
  const exactStatus: NoteStatus | undefined =
    filter !== 'all' && !showingDiscarded && !isIntentValue(filter) ? filter : undefined;
  const segmentValue = exactStatus !== undefined
    ? noteIntent(exactStatus)
    : isIntentValue(filter)
      ? filter
      : 'por_hacer';
  // `GET /notes` accepts only one exact status. Most intents span several statuses and cannot
  // be asked for server-side in a single request (see this task's reported schema gap), but an
  // intent that happens to name exactly one status - "En Excel" today - can still be pushed
  // down as a real server-side filter instead of an unfiltered fetch.
  const intentStatuses = isIntentValue(filter) ? statusesForIntent(filter) : undefined;
  const serverStatus: NoteStatus | undefined =
    exactStatus ?? (intentStatuses?.length === 1 ? intentStatuses[0] : undefined);

  const notesQuery = useInfiniteQuery({
    queryKey: ['notes', filter],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      fetchNotes({
        cursor: pageParam,
        limit: PAGE_SIZE,
        status: serverStatus,
        discarded: showingDiscarded,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      // Polling only ever resyncs a single already-fetched page (the first one). See
      // docs/implementation/06-mobile-web.md 6.3's pagination/polling stop conditions.
      if (pages.length > 1) {
        pollAttemptRef.current = 0;
        return false;
      }
      const rows = pages.flatMap((page) => page.items);
      const interval = computeRefetchIntervalMs(rows, pollAttemptRef.current);
      if (interval === false) {
        pollAttemptRef.current = 0;
        return false;
      }
      pollAttemptRef.current += 1;
      return interval;
    },
  });

  // A wide, unfiltered read of the intent counts (capped at one page) - the segment control's
  // counts always describe the whole inbox, not whatever the current filter narrowed to.
  const countsQuery = useQuery({
    queryKey: ['notes', 'counts'],
    queryFn: () => fetchNotes({ limit: 100 }),
  });

  useEffect(() => {
    pollAttemptRef.current = 0;
  }, [filter]);

  useEffect(() => {
    if (!restoredScroll.current && notesQuery.data !== undefined) {
      window.scrollTo(0, scrollYRef.current);
      restoredScroll.current = true;
    }
  }, [notesQuery.data]);

  useEffect(() => {
    function onScroll(): void {
      scrollYRef.current = window.scrollY;
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      saveNotesScreenState({ filter, scrollY: scrollYRef.current });
    };
  }, [filter]);

  const agentQuery = useQuery({ queryKey: ['agents', 'status'], queryFn: fetchAgentStatus });
  const customersCaptureQuery = useQuery({
    queryKey: ['references', 'customers', 'capture'],
    queryFn: () => fetchReferenceCapture('customers'),
  });
  const productsCaptureQuery = useQuery({
    queryKey: ['references', 'products', 'capture'],
    queryFn: () => fetchReferenceCapture('products'),
  });
  const alertCountQuery = useQuery({
    queryKey: ['alerts', 'open-count'],
    queryFn: fetchOpenAlertCount,
  });

  async function handleRetry(noteId: string): Promise<void> {
    setRetryStates((previous) => ({ ...previous, [noteId]: { kind: 'pending' } }));
    try {
      const result = await retryNote(noteId);
      queryClient.setQueryData<InfiniteData<Page<NoteSummary>>>(['notes', filter], (data) =>
        withUpdatedNoteStatus(data, noteId, result.status),
      );
      // The segmented control's counts come from their own query (`['notes', 'counts']`), not
      // from whatever list is on screen - patch it too, or the counts keep showing pre-retry
      // numbers until the page reloads even though the row already moved sections.
      queryClient.setQueryData<Page<NoteSummary>>(['notes', 'counts'], (data) =>
        withUpdatedNoteStatusInPage(data, noteId, result.status),
      );
      setRetryStates((previous) => withoutRetryState(previous, noteId));
    } catch (caught) {
      if (!(caught instanceof ApiError)) {
        throw caught;
      }
      const classification = classifyRetryFailure(caught);
      if (classification === 'session_expired') {
        await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY });
        setRetryStates((previous) => withoutRetryState(previous, noteId));
        return;
      }
      if (classification === 'soft_refresh') {
        setRetryStates((previous) => ({ ...previous, [noteId]: { kind: 'refreshing' } }));
        await notesQuery.refetch();
        setRetryStates((previous) => withoutRetryState(previous, noteId));
        return;
      }
      setRetryStates((previous) => ({
        ...previous,
        [noteId]: { kind: 'error', message: caught.message },
      }));
    }
  }

  async function handleRestore(noteId: string): Promise<void> {
    setRetryStates((previous) => ({ ...previous, [noteId]: { kind: 'pending' } }));
    try {
      await restoreNote(noteId);
      // The row belongs to the live list again, so it leaves this one. Both lists are refetched
      // rather than patched: a restore can re-queue work, and the row's status line is derived
      // from facts (open issues, alerts) this screen does not recompute locally.
      await queryClient.invalidateQueries({ queryKey: ['notes'] });
      setRetryStates((previous) => withoutRetryState(previous, noteId));
    } catch (caught) {
      if (!(caught instanceof ApiError)) {
        throw caught;
      }
      if (classifyRetryFailure(caught) === 'session_expired') {
        await queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY });
        setRetryStates((previous) => withoutRetryState(previous, noteId));
        return;
      }
      setRetryStates((previous) => ({
        ...previous,
        [noteId]: { kind: 'error', message: caught.message },
      }));
    }
  }

  // `GET /notes` only accepts one exact status, so an intent spanning several statuses
  // (`por_hacer`, `en_camino`) cannot be asked for server-side in one request - see this
  // task's reported schema gap. The fetched page is narrowed to the current intent
  // client-side; an exact-status filter already asked the server for exactly that status.
  const rawRows = notesQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const rows =
    filter === 'all' || showingDiscarded || exactStatus !== undefined
      ? rawRows
      : rawRows.filter((row) => noteIntent(row.status) === filter);
  const countRows = countsQuery.data?.items ?? [];
  const counts = countByIntent(countRows);
  const agent = agentIndicator(agentQuery.data, agentQuery.isError);
  const references = referenceIndicator(
    customersCaptureQuery.data,
    customersCaptureQuery.isError,
    productsCaptureQuery.data,
    productsCaptureQuery.isError,
  );
  const systemHealthy = agent.tone === 'ok' && references.tone === 'ok';
  const systemTone: 'ok' | 'warning' | 'unknown' =
    agent.tone === 'warning' || references.tone === 'warning'
      ? 'warning'
      : agent.tone === 'unknown' || references.tone === 'unknown'
        ? 'unknown'
        : 'ok';

  function emptyStateMessage(): string {
    if (showingDiscarded) {
      return 'No has descartado ninguna nota.';
    }
    if (filter === 'all') {
      return 'Aún no has subido ninguna nota. Ve a Captura para tomar la primera.';
    }
    if (exactStatus !== undefined) {
      return `No tienes notas en estado "${NOTE_STATUS_LABELS[exactStatus]}".`;
    }
    return `No tienes notas en "${NOTE_INTENT_LABELS[segmentValue]}".`;
  }

  function contextLine(): string {
    if (showingDiscarded) {
      return 'Notas que descartaste. Nada se borró: puedes recuperarlas.';
    }
    if (filter !== 'por_hacer') {
      return '';
    }
    const n = counts.por_hacer;
    if (n === 0) {
      return 'Nada necesita tu atención ahora mismo.';
    }
    return n === 1 ? '1 nota necesita algo de ti.' : `${String(n)} notas necesitan algo de ti.`;
  }

  return (
    <div className={styles.screen}>
      <div className={styles.headerRow}>
        <button
          type="button"
          className={styles.systemPill}
          onClick={() => {
            setSystemSheetOpen(true);
          }}
        >
          <span
            className={[
              styles.systemDot,
              systemTone === 'ok' ? styles.dotOk : systemTone === 'warning' ? styles.dotWarning : styles.dotUnknown,
            ].join(' ')}
            aria-hidden="true"
          />
          Sistema
        </button>
      </div>

      <SegmentedControl
        ariaLabel="Agrupar notas por intención"
        value={segmentValue}
        onChange={(value) => {
          setFilter(value);
        }}
        options={NOTE_INTENTS.map((intent) => ({
          value: intent,
          label: NOTE_INTENT_LABELS[intent],
          count: counts[intent],
          emphasizeCount: intent === 'por_hacer',
        }))}
      />

      <div className={styles.contextRow}>
        <span className={styles.contextText}>{contextLine()}</span>
        <button
          type="button"
          className={styles.filterLinkButton}
          onClick={() => {
            setFilterSheetOpen(true);
          }}
        >
          Filtrar ▾
        </button>
      </div>

      <div className={styles.alertLinkRow}>
        <Link className={styles.alertLink} to="/alertas">
          Alertas
          {alertCountQuery.data !== undefined && alertCountQuery.data > 0 ? (
            <span className={styles.alertCount}>{alertCountQuery.data}</span>
          ) : null}
        </Link>
      </div>

      {notesQuery.isPending ? (
        <p className={styles.inlineLoading} role="status" aria-live="polite">
          Cargando notas…
        </p>
      ) : notesQuery.isError && rows.length === 0 ? (
        <ErrorScreen
          message="No se pudieron cargar tus notas. Revisa tu conexión e intenta de nuevo."
          detail={notesQuery.error instanceof Error ? notesQuery.error.message : undefined}
          onRetry={() => {
            void notesQuery.refetch();
          }}
        />
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>
          <p>{emptyStateMessage()}</p>
          {filter === 'all' || filter === 'por_hacer' ? (
            <Link className={styles.alertLink} to="/capturar">
              Ir a Captura
            </Link>
          ) : null}
        </div>
      ) : (
        <>
          <ul className={styles.list}>
            {rows.map((note) => (
              <NoteRow
                key={note.id}
                note={note}
                retryState={retryStates[note.id] ?? { kind: 'idle' }}
                onRetry={(noteId) => {
                  void handleRetry(noteId);
                }}
                onRestore={(noteId) => {
                  void handleRestore(noteId);
                }}
              />
            ))}
          </ul>
          {notesQuery.hasNextPage ? (
            <button
              type="button"
              className={styles.loadMoreButton}
              disabled={notesQuery.isFetchingNextPage}
              onClick={() => {
                void notesQuery.fetchNextPage();
              }}
            >
              {notesQuery.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </button>
          ) : null}
        </>
      )}

      {filterSheetOpen ? (
        <Sheet
          title="Filtrar notas"
          onClose={() => {
            setFilterSheetOpen(false);
          }}
        >
          <p className={styles.sheetSectionHeader}>POR HACER</p>
          <ul className={styles.sheetStatusList}>
            {statusesForIntent('por_hacer').map((status) => (
              <li key={status}>
                <button
                  type="button"
                  className={[styles.sheetStatusRow, exactStatus === status ? styles.sheetStatusRowActive : null]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={exactStatus === status}
                  onClick={() => {
                    setFilter(status);
                    setFilterSheetOpen(false);
                  }}
                >
                  <span>{NOTE_STATUS_LABELS[status]}</span>
                  <span className={styles.sheetStatusCount}>
                    {countRows.filter((row) => row.status === status).length}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={styles.sheetSectionHeaderButton}
            onClick={() => {
              setEnCaminoExplainerOpen((open) => !open);
            }}
          >
            EN CAMINO · EN EXCEL {enCaminoExplainerOpen ? '▴' : '▾'}
          </button>
          {enCaminoExplainerOpen ? (
            <>
              <p className={styles.sheetExplainer}>
                Estos estados avanzan solos: el reconocimiento, el envío y la entrega a Excel. No
                necesitan que hagas nada ahora mismo.
              </p>
              <ul className={styles.sheetStatusList}>
                {[...statusesForIntent('en_camino'), ...statusesForIntent('en_excel')].map((status) => (
                  <li key={status}>
                    <button
                      type="button"
                      className={styles.sheetStatusRow}
                      onClick={() => {
                        setFilter(status);
                        setFilterSheetOpen(false);
                      }}
                    >
                      <span>{NOTE_STATUS_LABELS[status]}</span>
                      <span className={styles.sheetStatusCount}>
                        {countRows.filter((row) => row.status === status).length}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={styles.sheetSeeAllButton}
                onClick={() => {
                  setFilter('en_camino');
                  setFilterSheetOpen(false);
                }}
              >
                {`Ver ${String(counts.en_camino + counts.en_excel)} notas`}
              </button>
            </>
          ) : null}

          <button
            type="button"
            className={styles.sheetSeeAllButton}
            onClick={() => {
              setFilter('all');
              setFilterSheetOpen(false);
            }}
          >
            Ver todas
          </button>

          <p className={styles.sheetSectionHeader}>DESCARTADAS</p>
          <p className={styles.sheetExplainer}>
            Las notas que descartaste siguen guardadas con su foto y todo lo que se leyó de
            ellas. Puedes recuperarlas cuando quieras.
          </p>
          <button
            type="button"
            className={styles.sheetSeeAllButton}
            aria-pressed={showingDiscarded}
            onClick={() => {
              setFilter(showingDiscarded ? 'por_hacer' : DISCARDED_FILTER);
              setFilterSheetOpen(false);
            }}
          >
            {showingDiscarded ? 'Volver a las notas activas' : 'Ver descartadas'}
          </button>
        </Sheet>
      ) : null}

      {systemSheetOpen ? (
        <Sheet
          title="Sistema"
          onClose={() => {
            setSystemSheetOpen(false);
          }}
        >
          {systemHealthy ? (
            <p className={styles.systemReassurance}>
              <span className={`${styles.systemDot ?? ''} ${styles.dotOk ?? ''}`} aria-hidden="true" />
              Todo en orden: el agente de Excel está conectado y las listas de clientes y
              productos están al día.
            </p>
          ) : (
            <ul className={styles.systemList}>
              <li className={styles.systemListItem}>
                <span
                  className={[styles.systemDot, agent.tone === 'ok' ? styles.dotOk : styles.dotWarning].join(' ')}
                  aria-hidden="true"
                />
                <span>{agent.message}</span>
              </li>
              <li className={styles.systemListItem}>
                <span
                  className={[
                    styles.systemDot,
                    references.tone === 'ok' ? styles.dotOk : styles.dotWarning,
                  ].join(' ')}
                  aria-hidden="true"
                />
                <span>{references.message}</span>
              </li>
            </ul>
          )}
        </Sheet>
      ) : null}
    </div>
  );
}
